import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { fmtDate, fmtDateTime } from "../lib/date";
import api from "../lib/api";
import { SCHEMA, GROUPS } from "../config/adminSchema";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Archive, ArrowCounterClockwise, Trash, Copy, Tag as TagIcon,
  MagnifyingGlass, MapPin, Broadcast, QrCode, ArrowsClockwise, Prohibit, CheckCircle, Printer } from "@phosphor-icons/react";
import { PhotoUpload, EvidenceThumb } from "../components/Evidence";
import { NfcProgrammeDialog, NfcReasonDialog, NfcStatusBadge } from "../components/NfcTags";

function FieldInput({ field, value, onChange, refOptions }) {
  if (field.type === "photo")
    return <div className="flex items-center gap-3"><PhotoUpload category="reference" label="Photo" onUploaded={(f) => onChange(f.id)} />{value && <EvidenceThumb fileId={value} />}</div>;
  if (field.type === "bool")
    return <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid={`field-${field.name}`} checked={!!value} onChange={(e) => onChange(e.target.checked)} /> {field.label}</label>;
  if (field.type === "number")
    return <Input data-testid={`field-${field.name}`} type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))} />;
  if (field.type === "date")
    return <Input data-testid={`field-${field.name}`} type="date" value={value ?? ""} onChange={(e) => onChange(e.target.value || null)} />;
  if (field.type === "csvnum")
    return <Input data-testid={`field-${field.name}`} value={Array.isArray(value) ? value.join(", ") : (value ?? "")}
      onChange={(e) => onChange(e.target.value.split(",").map((x) => Number(x.trim())).filter((n) => !Number.isNaN(n)))} />;
  if (field.type === "textarea")
    return <Textarea data-testid={`field-${field.name}`} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
  if (field.type === "select")
    return <select data-testid={`field-${field.name}`} className="h-10 border-2 border-slate-300 rounded px-2 w-full" value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
      <option value="">—</option>{field.options.map((o) => <option key={o} value={o}>{o}</option>)}</select>;
  if (field.type === "ref")
    return <select data-testid={`field-${field.name}`} className="h-10 border-2 border-slate-300 rounded px-2 w-full" value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
      <option value="">—</option>{(refOptions || []).map((o) => <option key={o.id} value={o.id}>{o.name || o.label || o.id}</option>)}</select>;
  return <Input data-testid={`field-${field.name}`} value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
}

function ConfigManager({ resource }) {
  const schema = SCHEMA[resource];
  const [rows, setRows] = useState([]);
  const [refs, setRefs] = useState({});
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({});
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => { api.get(`/config/${resource}?include_archived=true`).then((r) => setRows(r.data)).catch(() => {}); }, [resource]);
  useEffect(() => {
    load();
    schema.fields.filter((f) => f.type === "ref").forEach((f) => {
      api.get(`/config/${f.resource}`).then((r) => setRefs((p) => ({ ...p, [f.resource]: r.data }))).catch(() => {});
    });
  }, [resource, load, schema]);

  const openNew = () => { setForm({}); setEditId(null); setDlg(true); };
  const openEdit = (row) => { setForm({ ...row }); setEditId(row.id); setDlg(true); };
  const save = async () => {
    try {
      if (editId) await api.put(`/config/${resource}/${editId}`, form);
      else await api.post(`/config/${resource}`, form);
      toast.success("Saved"); setDlg(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const act = async (row, action) => {
    try {
      if (action === "delete") await api.delete(`/config/${resource}/${row.id}`);
      else await api.post(`/config/${resource}/${row.id}/${action}`);
      toast.success(action); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const cell = (row, f) => {
    const v = row[f.name];
    if (f.type === "bool") return v ? "Yes" : "No";
    if (Array.isArray(v)) return v.join(", ");
    return String(v ?? "—");
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-xl font-black text-slate-900">{schema.label}</h2>
        {!schema.readOnly && <Button data-testid="config-new" onClick={openNew} className="bg-slate-900 hover:bg-slate-800 font-semibold"><Plus size={16} weight="bold" className="mr-1" />New</Button>}
      </div>
      <div className="bg-white border-2 border-slate-300 rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-100 border-b-2 border-slate-200 text-left font-body text-slate-600">
            <tr>{schema.fields.slice(0, 4).map((f) => <th key={f.name} className="px-3 py-2">{f.label}</th>)}<th className="px-3 py-2">State</th><th className="px-3 py-2"></th></tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} data-testid={`config-row-${row.id}`} className={`border-b border-slate-100 ${row.archived_at ? "opacity-50" : ""}`}>
                {schema.fields.slice(0, 4).map((f) => <td key={f.name} className="px-3 py-2">{cell(row, f)}</td>)}
                <td className="px-3 py-2">{row.archived_at ? "Archived" : "Active"}</td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-2">
                    {!schema.readOnly && <button data-testid={`edit-${row.id}`} onClick={() => openEdit(row)} className="text-blue-600"><PencilSimple size={18} weight="bold" /></button>}
                    {row.archived_at
                      ? <button data-testid={`restore-${row.id}`} onClick={() => act(row, "restore")} className="text-emerald-600"><ArrowCounterClockwise size={18} weight="bold" /></button>
                      : <button data-testid={`archive-${row.id}`} onClick={() => act(row, "archive")} className="text-amber-600"><Archive size={18} weight="bold" /></button>}
                    <button data-testid={`delete-${row.id}`} onClick={() => act(row, "delete")} className="text-red-600"><Trash size={18} weight="bold" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-white max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Edit" : "New"} — {schema.label}</DialogTitle></DialogHeader>
          <div className="flex flex-col gap-3">
            {schema.fields.map((f) => (
              <div key={f.name}>
                {f.type !== "bool" && <label className="font-body text-sm font-semibold text-slate-700">{f.label}</label>}
                <FieldInput field={f} value={form[f.name]} onChange={(v) => setForm((p) => ({ ...p, [f.name]: v }))} refOptions={refs[f.resource]} />
              </div>
            ))}
            <Button data-testid="config-save" onClick={save} className="bg-blue-600 hover:bg-blue-700 font-semibold">Save</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function QrImage({ tagId }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let url;
    api.get(`/tags/${tagId}/qr`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); });
    return () => url && URL.revokeObjectURL(url);
  }, [tagId]);
  return src ? <img src={src} alt="QR" className="w-40 h-40" data-testid={`qr-${tagId}`} /> : <div className="w-40 h-40 bg-slate-100" />;
}

const MEDIA_LABEL = { qr: "QR", nfc: "NFC", both: "NFC + QR" };

function TagManager({ presetTarget, onConsumePreset }) {
  const [tags, setTags] = useState([]);
  const [assets, setAssets] = useState([]);
  const [ramsList, setRamsList] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [detail, setDetail] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState({ type: "", status: "", media: "" });
  const [sort, setSort] = useState("issued");
  const [form, setForm] = useState({ target_type: "asset", target_id: "", description: "", position: "", media: "qr" });
  const [progTag, setProgTag] = useState(null);
  const [revokeTag, setRevokeTag] = useState(null);
  const [replaceTag, setReplaceTag] = useState(null);

  const load = () => api.get("/tags").then((r) => setTags(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/assets").then((r) => setAssets(r.data)).catch(() => {});
    api.get("/rams").then((r) => setRamsList(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (presetTarget) { setForm({ target_type: "asset", target_id: presetTarget, description: "", position: "", media: "qr" }); setDlg(true); onConsumePreset && onConsumePreset(); }
  }, [presetTarget, onConsumePreset]);

  const issue = async () => {
    try { await api.post("/tags", form); toast.success("Tag issued"); setDlg(false); setForm({ target_type: "asset", target_id: "", description: "", position: "", media: "qr" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const revoke = async (reason) => { try { await api.post(`/tags/${revokeTag.id}/revoke`, { reason }); toast.success("Revoked"); setRevokeTag(null); load(); setDetail(null); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const replace = async (reason) => { try { const r = await api.post(`/tags/${replaceTag.id}/replace`, { reason }); toast.success("Replacement issued — programme & verify it"); setReplaceTag(null); load(); setDetail(null); setProgTag(r.data); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };
  const programme = async (id) => { try { const r = await api.get(`/tags/${id}`); setProgTag(r.data); } catch (e) { toast.error("Failed"); } };
  const copy = (url) => { navigator.clipboard.writeText(url); toast.success("URL copied"); };
  const openDetail = async (id) => { try { const r = await api.get(`/tags/${id}`); setDetail(r.data); } catch (e) { toast.error("Failed"); } };
  const addToTarget = (t) => { setForm({ target_type: t.target_type, target_id: t.target_id, description: "", position: "", media: "qr" }); setDetail(null); setDlg(true); };

  const filtered = useMemo(() => {
    let out = tags.filter((t) => {
      if (filter.type && t.target_type !== filter.type) return false;
      if (filter.status === "active" && !t.is_active) return false;
      if (filter.status === "revoked" && t.is_active) return false;
      if (filter.media && (t.media || "qr") !== filter.media) return false;
      if (q) {
        const blob = `${t.tag_ref} ${t.target?.label || ""} ${t.target?.registration || ""} ${t.target?.make_model || ""} ${t.position || ""} ${t.target_type}`.toLowerCase();
        if (!blob.includes(q.toLowerCase())) return false;
      }
      return true;
    });
    const cmp = {
      issued: (a, b) => (b.issued_at || "").localeCompare(a.issued_at || ""),
      scanned: (a, b) => (b.last_scan_at || "").localeCompare(a.last_scan_at || ""),
      asset: (a, b) => (a.target?.asset_number || a.target?.label || "").localeCompare(b.target?.asset_number || b.target?.label || ""),
      ref: (a, b) => a.tag_ref.localeCompare(b.tag_ref),
    };
    return [...out].sort(cmp[sort] || cmp.issued);
  }, [tags, filter, q, sort]);

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h2 className="font-display text-xl font-black text-slate-900">NFC / QR Tag Manager</h2>
        <div className="flex gap-2">
          <Link to="/nfc/programme"><Button data-testid="tag-bulk-programme" variant="outline" className="font-semibold"><Broadcast size={16} weight="bold" className="mr-1" />Programme Tags</Button></Link>
          <Button data-testid="tag-new" onClick={() => { setForm({ target_type: "asset", target_id: "", description: "", position: "", media: "qr" }); setDlg(true); }} className="bg-slate-900 hover:bg-slate-800 font-semibold"><Plus size={16} weight="bold" className="mr-1" />Issue Tag</Button>
        </div>
      </div>

      <div className="bg-white border-2 border-slate-200 rounded-lg p-3 mb-3 flex flex-col gap-2">
        <div className="relative">
          <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input data-testid="tag-search" placeholder="Search tag ID, asset, registration, make/model, location…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-10 h-11 border-2" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <select data-testid="tag-filter-type" className="h-10 border-2 border-slate-300 rounded px-2 text-sm" value={filter.type} onChange={(e) => setFilter({ ...filter, type: e.target.value })}>
            <option value="">All targets</option><option value="asset">Assets</option><option value="rams">RAMS</option><option value="ppe_store">PPE Store</option><option value="document">Documents</option>
          </select>
          <select data-testid="tag-filter-status" className="h-10 border-2 border-slate-300 rounded px-2 text-sm" value={filter.status} onChange={(e) => setFilter({ ...filter, status: e.target.value })}>
            <option value="">Active + Revoked</option><option value="active">Active</option><option value="revoked">Revoked</option>
          </select>
          <select data-testid="tag-filter-media" className="h-10 border-2 border-slate-300 rounded px-2 text-sm" value={filter.media} onChange={(e) => setFilter({ ...filter, media: e.target.value })}>
            <option value="">Any media</option><option value="qr">QR</option><option value="nfc">NFC</option><option value="both">NFC + QR</option>
          </select>
          <select data-testid="tag-sort" className="h-10 border-2 border-slate-300 rounded px-2 text-sm" value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="issued">Sort: Issue date</option><option value="scanned">Sort: Last scanned</option><option value="asset">Sort: Asset</option><option value="ref">Sort: Tag ID</option>
          </select>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((t) => (
          <button key={t.id} data-testid={`tag-card-${t.id}`} onClick={() => openDetail(t.id)}
            className={`text-left bg-white border-2 rounded-lg p-4 hover:border-blue-500 transition-colors ${t.is_active ? "border-slate-300" : "border-red-300 opacity-70"}`}>
            <div className="flex items-center gap-2 mb-1">
              <TagIcon size={16} weight="bold" className="text-slate-500" />
              <span className="font-mono text-xs font-bold text-slate-700">{t.tag_ref}</span>
              <span className="ml-auto text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">{MEDIA_LABEL[t.media || "qr"]}</span>
              <NfcStatusBadge status={t.status} />
            </div>
            <div className="font-body font-semibold text-slate-900 text-sm">{t.target?.label || t.target_type}</div>
            {t.target?.sub && <div className="font-mono text-xs text-slate-500">{t.target.sub}</div>}
            {t.position && <div className="flex items-center gap-1 text-xs text-slate-600 mt-1"><MapPin size={12} weight="bold" />{t.position}</div>}
            <div className="font-mono text-[10px] text-slate-400 mt-2">
              Issued {t.issued_at ? fmtDate(t.issued_at) : "—"}{t.issued_by_name ? ` by ${t.issued_by_name}` : ""}
              {" · "}Last scan {t.last_scan_at ? fmtDateTime(t.last_scan_at) : "never"}
            </div>
          </button>
        ))}
        {filtered.length === 0 && <p className="text-slate-400">No tags match.</p>}
      </div>

      {/* Detail */}
      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-white max-h-[92vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader><DialogTitle className="font-display flex items-center gap-2"><TagIcon size={20} weight="bold" />{detail.tag_ref}</DialogTitle></DialogHeader>
              <div className="flex flex-col gap-3">
                <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                  <div className="text-xs font-bold uppercase text-slate-400 mb-1">Target</div>
                  <div className="font-body font-semibold text-slate-900">{detail.target?.label}</div>
                  {detail.target?.make_model && <div className="font-mono text-sm text-slate-600">{detail.target.make_model}</div>}
                  {detail.position && <div className="flex items-center gap-1 text-sm text-slate-600 mt-1"><MapPin size={14} weight="bold" />{detail.position}</div>}
                  <div className="flex items-center gap-2 mt-2 text-xs">
                    <span className="inline-flex items-center gap-1"><Broadcast size={13} weight="bold" />{MEDIA_LABEL[detail.media || "qr"]}</span>
                    <NfcStatusBadge status={detail.status} />
                    {detail.replaces_tag_ref && <span className="text-slate-500">Replaces {detail.replaces_tag_ref}</span>}
                    {detail.replaced_by_tag_ref && <span className="text-slate-500">Replaced by {detail.replaced_by_tag_ref}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <QrImage tagId={detail.id} />
                  <div className="min-w-0">
                    <div className="text-xs font-bold uppercase text-slate-400">Permanent URL</div>
                    <div className="font-mono text-[11px] text-slate-600 break-all">{detail.url}</div>
                    <div className="mt-2 text-xs font-mono text-slate-400">
                      Issued {detail.issued_at ? fmtDateTime(detail.issued_at) : "—"}<br />
                      Last scanned {detail.last_scan_at ? fmtDateTime(detail.last_scan_at) : "never"}
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <Button data-testid="tag-programme-nfc" onClick={() => programme(detail.id)} className="bg-blue-600 font-semibold"><Broadcast size={16} weight="bold" className="mr-1" />Programme NFC</Button>
                  <Button data-testid="tag-open" onClick={() => window.open(detail.url, "_blank")} className="bg-slate-700 font-semibold"><CheckCircle size={16} weight="bold" className="mr-1" />Open / Test</Button>
                  <Button data-testid="tag-copy" onClick={() => copy(detail.url)} className="bg-blue-600 font-semibold"><Copy size={16} weight="bold" className="mr-1" />Copy URL</Button>
                  <Button data-testid="tag-print" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/tags/${detail.id}/qr`, "_blank")} className="bg-slate-700 font-semibold"><Printer size={16} weight="bold" className="mr-1" />Print QR</Button>
                  <Button data-testid="tag-add-same" onClick={() => addToTarget(detail)} className="bg-slate-900 font-semibold"><Plus size={16} weight="bold" className="mr-1" />Add another</Button>
                  {detail.is_active && <Button data-testid="tag-replace" onClick={() => setReplaceTag(detail)} className="bg-amber-500 text-slate-900 font-semibold"><ArrowsClockwise size={16} weight="bold" className="mr-1" />Replace</Button>}
                  {detail.is_active && <Button data-testid="tag-revoke" onClick={() => setRevokeTag(detail)} className="bg-red-600 font-semibold"><Prohibit size={16} weight="bold" className="mr-1" />Revoke</Button>}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Issue */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-display">Issue Tag</DialogTitle></DialogHeader>
          <label className="font-body text-sm font-semibold">Target type</label>
          <select data-testid="tag-target-type" className="h-10 border-2 border-slate-300 rounded px-2" value={form.target_type} onChange={(e) => setForm({ ...form, target_type: e.target.value, target_id: "" })}>
            <option value="asset">Asset</option><option value="rams">RAMS / Policy</option><option value="document">Document</option><option value="ppe_store">PPE Store</option>
          </select>
          {form.target_type === "asset" && (
            <select data-testid="tag-target-asset" className="h-10 border-2 border-slate-300 rounded px-2" value={form.target_id} onChange={(e) => setForm({ ...form, target_id: e.target.value })}>
              <option value="">Select asset…</option>{assets.map((a) => <option key={a.id} value={a.id}>{a.asset_number} · {a.name}</option>)}
            </select>
          )}
          {form.target_type === "rams" && (
            <select data-testid="tag-target-rams" className="h-10 border-2 border-slate-300 rounded px-2" value={form.target_id} onChange={(e) => setForm({ ...form, target_id: e.target.value })}>
              <option value="">Select RAMS…</option>{ramsList.map((r) => <option key={r.id} value={r.id}>{r.ref} · {r.title}</option>)}
            </select>
          )}
          {form.target_type !== "asset" && form.target_type !== "rams" && <Input data-testid="tag-target-id" placeholder="Target record ID / label" value={form.target_id} onChange={(e) => setForm({ ...form, target_id: e.target.value })} />}
          <Input data-testid="tag-position" placeholder="Tag position / location (e.g. Driver Cab, Service Panel)" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} />
          <select data-testid="tag-media" className="h-10 border-2 border-slate-300 rounded px-2" value={form.media} onChange={(e) => setForm({ ...form, media: e.target.value })}>
            <option value="qr">QR</option><option value="nfc">NFC</option><option value="both">NFC + QR</option>
          </select>
          <Input data-testid="tag-desc" placeholder="Notes (optional)" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          <Button data-testid="tag-issue" onClick={issue} disabled={!form.target_id} className="bg-blue-600 hover:bg-blue-700 font-semibold">Issue &amp; Generate QR</Button>
        </DialogContent>
      </Dialog>

      {progTag && <NfcProgrammeDialog tag={progTag} open={!!progTag} onClose={() => { setProgTag(null); load(); }} onUpdated={() => {}} />}
      <NfcReasonDialog open={!!revokeTag} onClose={() => setRevokeTag(null)} onConfirm={revoke} title={`Revoke ${revokeTag?.tag_ref || "tag"}`} confirmLabel="Revoke Tag" danger />
      <NfcReasonDialog open={!!replaceTag} onClose={() => setReplaceTag(null)} onConfirm={replace} title={`Replace ${replaceTag?.tag_ref || "tag"}`} confirmLabel="Create Replacement" />
    </div>
  );
}

export default function AdminPage() {
  const [active, setActive] = useState("__tags");
  return (
    <div className="flex flex-col md:flex-row gap-6">
      <aside className="md:w-64 flex-shrink-0">
        <h1 className="font-display text-2xl font-black tracking-tight text-slate-900 mb-4">Admin &amp; Configuration</h1>
        <button data-testid="admin-nav-tags" onClick={() => setActive("__tags")}
          className={`w-full text-left px-3 py-2 rounded font-body text-sm font-semibold mb-2 ${active === "__tags" ? "bg-blue-600 text-white" : "bg-white border border-slate-200"}`}>
          NFC / QR Tag Manager
        </button>
        <Link to="/admin/data" data-testid="admin-nav-data"
          className="flex items-center gap-2 w-full text-left px-3 py-2 rounded font-body text-sm font-semibold mb-2 bg-red-50 border border-red-200 text-red-700 hover:bg-red-100">
          <Trash size={15} weight="bold" /> Data &amp; Cleanup
        </Link>
        {GROUPS.map((g) => (
          <div key={g.title} className="mb-3">
            <div className="font-body text-xs font-bold uppercase text-slate-400 px-1 mb-1">{g.title}</div>
            <div className="flex flex-col gap-1">
              {g.items.map((r) => (
                <button key={r} data-testid={`admin-nav-${r}`} onClick={() => setActive(r)}
                  className={`text-left px-3 py-1.5 rounded font-body text-sm ${active === r ? "bg-slate-900 text-white" : "hover:bg-white text-slate-700"}`}>
                  {SCHEMA[r].label}
                </button>
              ))}
            </div>
          </div>
        ))}
      </aside>
      <div className="flex-1 min-w-0">
        {active === "__tags" ? <TagManager /> : <ConfigManager key={active} resource={active} />}
      </div>
    </div>
  );
}
